import React, { useEffect, useRef, useState, useContext } from 'react';
import { useRouter } from 'next/router';
import { stringify } from 'query-string';
import onClickOutside from 'react-onclickoutside';
import classNames from 'classnames';

import { CustomerContext } from 'context/customer';
import { writeAddressToLocalStorage, readFulfillmentFromLocalStorage } from 'helpers/localStorage';
import { logMparticleEvent, mParticleCustomEvents, mParticleEventTypes } from 'helpers/mParticle';

import { ModalContext } from 'context/modal';

import ModalComponents from 'types/modals';

import IconGeoLocation from 'components/shared/SVG/IconGeoLocation';
import DefaultDropdown from './DefaultDropdown/DefaultDropdown';

import styles from './LocationSearch.module.scss';

function NoResults() {
  return (
    <div className={styles.noResultsWrapper} data-testid="NoResults">
      <p className={styles.noResultsText}>Could not find that location. Please try another.</p>
    </div>
  );
}

const WrappedNoResults = onClickOutside(NoResults);

type UpdateArrowButtonState = (a: boolean) => void;

interface LocationSearchProps {
  cssModifierDefaultDropdown?: string;
  cssModifierGoogleIcon?: string;
  cssModifierGoogleInput?: string;
  initialAddress: string;
  mparticleSource: string;
  updateArrowButtonState: UpdateArrowButtonState;
}

export default function LocationSearch({
  cssModifierDefaultDropdown,
  cssModifierGoogleIcon,
  cssModifierGoogleInput,
  initialAddress,
  mparticleSource,
  updateArrowButtonState,
}: LocationSearchProps) {
  const router = useRouter();
  const isSearchPage = router.route === '/search';
  const [searchValue, setSearchValue] = useState(initialAddress);
  const [showSavedAddresses, setShowSavedAddresses] = useState(false);
  const [showNoResults, setShowNoResults] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { hideModal, modalType } = useContext(ModalContext);
  const { user } = useContext(CustomerContext);

  async function handleSelectAddress(address: { formatted_address: string; id: string }) {
    if (!address) return;

    const formattedAddress = address.formatted_address;
    writeAddressToLocalStorage(address, address.id);
    router.push({
      pathname: '/search',
      search: stringify({ loc: formattedAddress, ...readFulfillmentFromLocalStorage() }),
    });
    hideModal();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function onChangeLocation(autocomplete: { getPlace: () => any }) {
    const place = autocomplete.getPlace();

    // If the user presses the enter key, and there is no formatted address,
    // this prompts the user to try another address
    if (!place.formatted_address) return;

    const formattedAddress = place.formatted_address;
    writeAddressToLocalStorage(place);
    router.push({
      pathname: '/search',
      search: stringify({ loc: formattedAddress, ...readFulfillmentFromLocalStorage() }),
    });
    setSearchValue(formattedAddress);
    logMparticleEvent({
      eventName: mParticleCustomEvents.performLocationSearch,
      eventType: mParticleEventTypes.Other,
      attributes: {
        source: mparticleSource,
        location:
          mparticleSource === 'homepage'
            ? 'google_autocomplete_hero_search_bar'
            : 'google_autocomplete_search_listing_modal',
        searched_location: formattedAddress,
        searched_latitude: place?.geometry?.location?.lat().toString(),
        searched_longitude: place?.geometry?.location?.lng().toString(),
        user_is_authenticated: !!user,
      },
    });

    // Todo, hide this behind a flag? Harmless for now...
    hideModal();
  }

  function onChangeSearchValue(event: React.ChangeEvent<HTMLInputElement>) {
    setSearchValue(event.target.value);
    if (mparticleSource === 'homepage') {
      // The arrow button on the home page is enabled if there is a valid initial address
      // or formatted address. We want to disable the button if the user input is not a
      // valid formatted address.
      if (event.target.value === initialAddress) {
        updateArrowButtonState(true);
      } else {
        updateArrowButtonState(false);
      }
    }
    const dropdownList = document.getElementsByClassName('pac-container')[0];
    setTimeout(() => {
      if (event.target.value) {
        if (dropdownList.childElementCount === 0) {
          setShowNoResults(true);
          setShowSavedAddresses(false);
        } else {
          setShowSavedAddresses(false);
          setShowNoResults(false);
        }
      } else {
        setShowSavedAddresses(true);
        setShowNoResults(false);
      }
    }, 100);
  }

  function handleFocus(event: React.FocusEvent<HTMLInputElement, Element>) {
    const dropdownList = document.getElementsByClassName('pac-container')[0];
    const allDropDowns = document.getElementsByClassName('pac-container');

    // only do this on search
    // https://chownow.atlassian.net/browse/CN-28721
    // Ensure the z-index on the search page will beat the nav and the modal background
    // We need to loop ALL potential dropdown containers and google can randomly replace these
    if (isSearchPage) {
      for (let i = 0; i < allDropDowns.length; i += 1) {
        const el = allDropDowns[i];
        el.classList.add('googleGeocodeResultsToFront');
      }
    }
    setTimeout(() => {
      if (event.target.value === '') {
        setShowSavedAddresses(true);
      } else if (event.target.value && dropdownList.childElementCount === 0) {
        setShowNoResults(true);
      }
    }, 100);
  }

  function handleDefaultDropdown() {
    setShowSavedAddresses(false);
  }

  useEffect(() => {
    const options = {
      componentRestrictions: { country: 'us' },
      fields: [
        'address_component',
        'adr_address',
        'formatted_address',
        'geometry',
        'name',
        'place_id',
        'scope',
        'type',
        'url',
        'utc_offset_minutes',
        'vicinity',
      ],
      types: ['geocode'],
    };
    const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, options);
    autocomplete.addListener('place_changed', () => onChangeLocation(autocomplete));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Update search term when when current location is fetched and initial address is updated
    if (searchValue !== initialAddress) {
      setSearchValue(initialAddress);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAddress]);

  const inputClass = classNames(styles.googleInput, cssModifierGoogleInput);

  const iconClass = classNames(styles.leadingIcon, cssModifierGoogleIcon);

  const testId =
    mparticleSource === 'homepage' ? 'HomepageLocationSearch' : 'SearchListingLocationSearch';

  return (
    <div className={styles.locationSearch}>
      <input
        autoComplete="off"
        data-testid={testId}
        type="search"
        name="location search"
        value={searchValue}
        onChange={(e) => onChangeSearchValue(e)}
        placeholder="Enter city or address"
        ref={inputRef}
        className={inputClass}
        onFocus={(e) => handleFocus(e)}
        disabled={modalType === ModalComponents.AUTH} // to prevent address autofill when auth modal open
      />
      <IconGeoLocation className={iconClass} />
      {showSavedAddresses && (
        <DefaultDropdown
          cssModifier={cssModifierDefaultDropdown}
          onSelectAddress={handleSelectAddress}
          hideDefaultDropdown={handleDefaultDropdown}
          handleClickOutside={handleDefaultDropdown}
          mparticleSource={mparticleSource}
        />
      )}
      {showNoResults && <WrappedNoResults handleClickOutside={() => setShowNoResults(false)} />}
    </div>
  );
}
